import Modal from "react-bootstrap/Modal";
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "../../Style/Checkbox.scss";
import { errorAlert, handleSuccess } from "../../Components/Alerts";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import "../../Style/__button.scss";
import "../ClientsCRUD/ClientsModal.scss";
import "./OtherAccountsModal.scss";
import TextField from "@mui/material/TextField";
import { Button } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Select from "react-select";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { AddElementsButton } from "../../Components/AddElementsButton";

function InsertAccounts(props) {
    const headers = authHeader();
    const isEditMode = props.mode === "edit";

    const normalizeId = (value) => {
        if (value == null || value === "") {
            return "";
        }

        return String(value);
    };

    const getApiErrorMessage = (error) => {
        if (error?.response?.data?.message) {
            return error.response.data.message;
        }

        if (
            typeof error?.response?.data === "string" &&
            error.response.data.trim() !== ""
        ) {
            return error.response.data;
        }

        if (error?.message) {
            return error.message;
        }

        return "Ka ndodhur një gabim gjatë përpunimit të kërkesës.";
    };

    const [accountID, setAccountID] = useState(null);
    const [accName, setAccName] = useState("");
    const [ledgerID, setLedgerID] = useState("");
    const [ledgerName, setLedgerName] = useState("");
    const [comment, setComment] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [ledger, setLedger] = useState([]);
    const [filteredLedgers, setFilteredLedgers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    const [accountNameError, setAccountNameError] = useState("");
    const [ledgerError, setLedgerError] = useState("");

    const initialSnapshotRef = useRef(null);
    const [showConfirmClose, setShowConfirmClose] = useState(false);

    const resetForm = () => {
        setAccountID(null);
        setAccName("");
        setLedgerID("");
        setLedgerName("");
        setComment("");
        setIsActive(true);
        setSearchTerm("");
        setAccountNameError("");
        setLedgerError("");
        setIsSubmitting(false);
        initialSnapshotRef.current = null;
        setShowConfirmClose(false);
    };

    const setFormFromAccount = (account) => {
        const nextAccountName = account?.AccountName ?? "";
        const nextLedgerID = account?.LedgerID ?? "";
        const nextLedgerName = account?.LedgerNameEN ?? "";
        const nextComment = account?.Comment ?? "";
        const nextIsActive =
            typeof account?.IsActive === "boolean" ? account.IsActive : true;

        setAccountID(account?.AccountID ?? null);
        setAccName(nextAccountName);
        setLedgerID(nextLedgerID);
        setLedgerName(nextLedgerName);
        setComment(nextComment);
        setIsActive(nextIsActive);
        setSearchTerm("");
        setAccountNameError("");
        setLedgerError("");
        setIsSubmitting(false);

        initialSnapshotRef.current = {
            accName: nextAccountName,
            ledgerID: nextLedgerID,
            comment: nextComment,
            isActive: nextIsActive,
        };
    };

    useEffect(() => {
        axios
            .get(ApiLink.API_URL + "ledger", { headers })
            .then((response) => {
                setLedger(response.data);
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    useEffect(() => {
        if (!props.show) {
            return;
        }

        if (isEditMode) {
            if (!props.initialData) {
                return;
            }

            setFormFromAccount(props.initialData);
            return;
        }

        resetForm();
    }, [props.show, props.initialData, isEditMode]);

    useEffect(() => {
        if (!isEditMode || !ledgerID || ledgerName) {
            return;
        }

        const matchedLedger = ledger.find(
            (entry) => normalizeId(entry.LedgerID) === normalizeId(ledgerID),
        );
        if (matchedLedger) {
            setLedgerName(matchedLedger.LedgerNameEN);
        }
    }, [ledger, ledgerID, ledgerName, isEditMode]);

    useEffect(() => {
        setFilteredLedgers(
            ledger.filter((data) =>
                data.LedgerNameEN.toLowerCase().includes(searchTerm.toLowerCase()),
            ),
        );
    }, [searchTerm, ledger]);

    const handleInputChange = (newValue) => {
        const inputValue = newValue.replace(/\W/g, "");
        setSearchTerm(inputValue);
    };

    const handleFilterLedger = (selectedOption) => {
        if (selectedOption && selectedOption.LedgerID) {
            setLedgerID(selectedOption.LedgerID);
            setLedgerName(selectedOption.label);
            setLedgerError("");
        } else {
            setLedgerID("");
            setLedgerName("");
        }
    };

    const selectedLedgerOption = useMemo(() => {
        if (!ledgerID) {
            return null;
        }

        const matchedLedger = ledger.find(
            (entry) => normalizeId(entry.LedgerID) === normalizeId(ledgerID),
        );

        if (matchedLedger) {
            return {
                label: matchedLedger.LedgerNameEN,
                value: matchedLedger.LedgerID,
                LedgerID: matchedLedger.LedgerID,
            };
        }

        if (ledgerName) {
            return {
                label: ledgerName,
                value: ledgerID,
                LedgerID: ledgerID,
            };
        }

        return null;
    }, [ledger, ledgerID, ledgerName]);

    const isDirty = useMemo(() => {
        if (!isEditMode) {
            return accName.trim() !== "" || ledgerID !== "" || comment !== "";
        }

        const snapshot = initialSnapshotRef.current;
        if (!snapshot) {
            return false;
        }

        return (
            accName !== snapshot.accName ||
            ledgerID !== snapshot.ledgerID ||
            comment !== snapshot.comment ||
            isActive !== snapshot.isActive
        );
    }, [isEditMode, accName, ledgerID, comment, isActive]);

    const handleSubmit = (event) => {
        event.preventDefault();
        if (isSubmitting) {
            return;
        }

        const trimmedAccountName = accName.trim();
        const trimmedComment = comment.trim();
        let hasValidationError = false;

        if (trimmedAccountName === "") {
            setAccountNameError("Plotësoni emrin e llogarisë");
            hasValidationError = true;
        } else {
            setAccountNameError("");
        }

        if (ledgerID == null || ledgerID === "") {
            setLedgerError("Plotësoni fushën e llogarisë kontabël");
            hasValidationError = true;
        } else {
            setLedgerError("");
        }

        if (hasValidationError) {
            return;
        }

        setIsSubmitting(true);

        const insertPayload = {
            AccountName: trimmedAccountName,
            LedgerID: ledgerID,
            Comment: trimmedComment,
            IsActive: true,
        };

        const updatePayload = {
            AccountID: accountID,
            AccountName: trimmedAccountName,
            LedgerID: ledgerID,
            Comment: trimmedComment,
            IsActive: isActive,
        };

        const submitRequest = isEditMode
            ? axios.put(ApiLink.API_URL + "Accounts/editOtherAccounts", updatePayload, {
                    headers,
                })
            : axios.post(ApiLink.API_URL + "Accounts/createOtherAccounts", insertPayload, {
                    headers,
                });

        submitRequest
            .then(() => {
                handleSuccess(
                    isEditMode
                        ? "Llogaria u përditësua me sukses"
                        : "Llogaria u shtua me sukses",
                );
                resetForm();
                props.onHide();
                props.refreshData();
            })
            .catch((error) => {
                console.error(error);
                errorAlert(getApiErrorMessage(error));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const doClose = () => {
        if (isSubmitting) {
            return;
        }

        props.onHide();
        resetForm();
    };

    const handleClose = () => {
        if (isSubmitting) {
            return;
        }

        if (isDirty) {
            setShowConfirmClose(true);
            return;
        }

        doClose();
    };

    return (
        <>
            <Modal
                {...props}
                onHide={handleClose}
                backdrop={isSubmitting ? "static" : true}
                keyboard={!isSubmitting}
                size="lg"
                dialogClassName="clients-modal-dialog other-accounts-modal-dialog"
                contentClassName="clients-modal-content other-accounts-modal-content"
                aria-labelledby="contained-modal-title-vcenter"
                centered
            >
                <Modal.Header className="clients-modal-header">
                    <div className="clients-modal-header-content">
                        <Modal.Title
                            id="contained-modal-title-vcenter"
                            className="clients-modal-title"
                        >
                            {isEditMode
                                ? "Përditëso llogarinë tjetër"
                                : "Krijo llogari tjetër"}
                        </Modal.Title>
                        <p className="clients-modal-subtitle">
                            {isEditMode
                                ? "Ndrysho të dhënat e llogarisë dhe ruaji për përditësim në sistem."
                                : "Plotësoni të dhënat e llogarisë dhe ruani për ta shtuar në sistem."}
                        </p>
                        <div className="clients-modal-meta">
                            <span className="clients-meta-pill">Fushat me * janë obligative</span>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body className="clients-modal-body">
                    <form onSubmit={handleSubmit} className="clients-form">
                        <section className="clients-section">
                            <h6 className="clients-section-title">Informata Kryesore</h6>
                            <div className="clients-grid clients-grid-2">
                                <div className="clients-field clients-field-full">
                                    <TextField
                                        fullWidth
                                        required
                                        id="AccountName"
                                        label="Emri i Llogarisë *"
                                        variant="outlined"
                                        type="text"
                                        name="AccountName"
                                        value={accName}
                                        onChange={(event) => {
                                            setAccName(event.target.value);
                                            if (accountNameError) {
                                                setAccountNameError("");
                                            }
                                        }}
                                        autoComplete="off"
                                        size="small"
                                    />
                                    <span className="clients-error">
                                        {accountNameError && `${accountNameError}`}
                                    </span>
                                </div>

                                <div className="clients-field clients-field-full">
                                    <label className="clients-select-label">
                                        Përzgjedh Llogarinë Kontabël *
                                    </label>
                                    <Select
                                        inputId="LedgerID"
                                        classNamePrefix="clients-select"
                                        value={selectedLedgerOption}
                                        options={filteredLedgers.map((data) => ({
                                            label: data.LedgerNameEN,
                                            value: data.LedgerID,
                                            LedgerID: data.LedgerID,
                                        }))}
                                        onInputChange={handleInputChange}
                                        onChange={handleFilterLedger}
                                        isClearable
                                        placeholder="Select"
                                        noOptionsMessage={() => "Nuk u gjet asnjë përputhje"}
                                        components={{ IndicatorSeparator: () => null }}
                                    />
                                    <span className="clients-error">
                                        {ledgerError && `${ledgerError}`}
                                    </span>
                                </div>
                            </div>
                        </section>

                        <section className="clients-section">
                            <h6 className="clients-section-title">Koment</h6>
                            <div className="clients-grid clients-grid-2">
                                <div className="clients-field clients-field-full">
                                    <textarea
                                        rows={3}
                                        className="clients-notes"
                                        id="Comment"
                                        placeholder="Komenti"
                                        name="Comment"
                                        value={comment}
                                        onChange={(event) => setComment(event.target.value)}
                                        autoComplete="off"
                                    />
                                </div>

                                {isEditMode && (
                                    <div className="clients-field clients-field-full">
                                        <label className="clients-active-toggle">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(isActive)}
                                                onChange={(event) => setIsActive(event.target.checked)}
                                                className="clients-active-checkbox"
                                                disabled={isSubmitting}
                                            />
                                            <span className="clients-active-copy">
                                                <span className="clients-active-title">Statusi i llogarisë</span>
                                                <span className="clients-active-description">
                                                    {isActive ? "Llogaria është aktive në sistem" : "Llogaria është joaktive në sistem"}
                                                </span>
                                            </span>
                                        </label>
                                    </div>
                                )}
                            </div>
                        </section>

                        <div className="clients-modal-footer">
                            <Button
                                type="submit"
                                variant="contained"
                                className="clients-save-btn"
                                disabled={isSubmitting || (isEditMode && !isDirty)}
                            >
                                {isSubmitting
                                    ? "Duke ruajtur..."
                                    : isEditMode
                                        ? "Përditëso & Mbyll"
                                        : "Ruaj & Mbyll"}
                            </Button>
                            <Button
                                variant="outlined"
                                className="clients-close-btn"
                                onClick={handleClose}
                                disabled={isSubmitting}
                            >
                                Mbyll
                            </Button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>

            <Dialog
                open={showConfirmClose}
                onClose={() => setShowConfirmClose(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Konfirmo mbylljen</DialogTitle>
                <DialogContent>
                    Keni ndryshime të paruajtura. Nëse mbyllni tani, të dhënat e ndryshuara do të humbin. A jeni të sigurt?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowConfirmClose(false)} variant="outlined">
                        Kthehu
                    </Button>
                    <Button onClick={doClose} variant="contained" color="error">
                        Po, mbyll pa ruajtur
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default function OtherAccountsModal(props) {
    const [internalModalShow, setInternalModalShow] = React.useState(false);
    const isControlled = typeof props.show === "boolean";
    const modalShow = isControlled ? props.show : internalModalShow;

    const openModal = () => {
        if (isControlled) {
            return;
        }

        setInternalModalShow(true);
    };

    const closeModal = () => {
        if (isControlled) {
            if (props.onHide) {
                props.onHide();
            }
            return;
        }

        setInternalModalShow(false);
    };

    const handleRefreshData = () => {
        if (props.fetchData) {
            props.fetchData();
        }
    };

    return (
        <>
            {!isControlled &&
                (props.type === "text" ? (
                    <AddElementsButton
                        text="Krijo Llogari tjeter"
                        onClick={openModal}
                        icon="bi bi-plus-circle"
                        className="button-68"
                    />
                ) : props.type === "insert" ? (
                    <AddElementsButton
                        text="Krijo Llogari tjeter"
                        onClick={openModal}
                        icon="bi bi-plus-circle"
                        className="button-15"
                    />
                ) : (
                    <AddBoxIcon
                        style={{ height: "30px", color: "black" }}
                        onClick={openModal}
                    />
                ))}

            <InsertAccounts
                show={modalShow}
                onHide={closeModal}
                refreshData={handleRefreshData}
                mode={props.mode}
                initialData={props.initialData}
            />
        </>
    );
}
